import React from "react";
import {
  Box,
  Stack,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";

/**
 * DevIdeToolbar
 *
 * Stateless toolbar used by DevIdePanel.
 * All state lives in the parent; this component just calls the handlers
 * passed in via props.
 */
const DevIdeToolbar = ({
  backendActionLoading,
  frontendActionLoading,
  actionMessage,

  // NEW: projects
  projects,
  selectedProjectId,
  setSelectedProjectId,
  handleOpenNewApp,

  // module + filter
  selectedModule,
  modules,
  setSelectedModule,
  filterText,
  setFilterText,

  // change management
  changes,
  activeChange,
  handleSelectActiveChange,
  changeFilterMode,
  setChangeFilterMode,
  handleCreateNewChange,
  handleRevertWholeChange,
  revertWholeChangeLoading,

  // sandbox actions
  handleRestartSandboxBackend,
  handleRebuildSandboxFrontend,
  handleAgentExportPackage,
}) => {
  const activeChangeId = activeChange && activeChange.id ? activeChange.id : "";

  const handleProjectChange = (event) => {
    const value = event.target.value;
    if (typeof setSelectedProjectId === "function") {
      setSelectedProjectId(value);
    }
  };

  const handleModuleChange = (event) => {
    const value = event.target.value;
    if (setSelectedModule) {
      setSelectedModule(value);
    }
  };

  const handleFilterModeChange = (event) => {
    const value = event.target.value;
    if (setChangeFilterMode) {
      setChangeFilterMode(value);
    }
  };

  const handleFilterTextChange = (event) => {
    if (setFilterText) {
      setFilterText(event.target.value);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
      {/* Row 0: project selector */}
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="dev-ide-project-label">Project</InputLabel>
          <Select
            labelId="dev-ide-project-label"
            label="Project"
            value={selectedProjectId || ""}
            onChange={handleProjectChange}
          >
            {projects && projects.length > 0 ? (
              projects.map((p) => (
                <MenuItem key={p.project_id} value={p.project_id}>
                  {p.label ? `${p.label}` : p.project_id}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="">
                <em>No projects</em>
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {selectedProjectId ? (
          <Typography variant="caption" color="text.secondary">
            Active project_id: {selectedProjectId}
          </Typography>
        ) : null}

        <Button
          variant="contained"
          size="small"
          onClick={handleOpenNewApp}
          sx={{ ml: "auto" }}
        >
          NEW APP
        </Button>
      </Stack>

      {/* Top row: sandbox actions */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="outlined"
          size="small"
          onClick={handleRestartSandboxBackend}
          disabled={backendActionLoading}
        >
          {backendActionLoading ? "Restarting Flask..." : "RESTART SANDBOX FLASK"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleRebuildSandboxFrontend}
          disabled={frontendActionLoading}
        >
          {frontendActionLoading ? "Rebuilding frontend..." : "REBUILD SANDBOX FRONTEND"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleAgentExportPackage}
          disabled={!activeChangeId}
        >
          AGENT EXPORT PACKAGE
        </Button>
      </Stack>

      {/* Second row: module + text filter */}
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="dev-ide-module-label">Module</InputLabel>
          <Select
            labelId="dev-ide-module-label"
            label="Module"
            value={selectedModule || "ALL"}
            onChange={handleModuleChange}
          >
            <MenuItem value="ALL">All modules</MenuItem>
            {modules &&
              modules.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          fullWidth
          placeholder="Filter by module, path, description..."
          value={filterText}
          onChange={handleFilterTextChange}
        />
      </Stack>

      {/* Third row: change selection + file filter mode */}
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="dev-ide-change-label">Change</InputLabel>
          <Select
            labelId="dev-ide-change-label"
            label="Change"
            value={activeChangeId}
            onChange={handleSelectActiveChange}
          >
            <MenuItem value="">
              <em>No active change</em>
            </MenuItem>
            {changes &&
              changes.map((chg) => (
                <MenuItem key={chg.id} value={chg.id}>
                  #{chg.id} {chg.title || ""}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="dev-ide-file-filter-label">Files</InputLabel>
          <Select
            labelId="dev-ide-file-filter-label"
            label="Files"
            value={changeFilterMode || "ALL"}
            onChange={handleFilterModeChange}
          >
            <MenuItem value="ALL">All files</MenuItem>
            <MenuItem value="ACTIVE_CHANGE_ONLY">Active change only</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          size="small"
          onClick={handleCreateNewChange}
          sx={{ ml: 1 }}
        >
          NEW CHANGE
        </Button>

        <Button
          variant="outlined"
          color="warning"
          size="small"
          onClick={handleRevertWholeChange}
          disabled={!activeChangeId || revertWholeChangeLoading}
        >
          {revertWholeChangeLoading ? "Reverting..." : "REVERT CHANGE"}
        </Button>
      </Stack>

      {/* Status / action message */}
      {actionMessage && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", display: "block", maxHeight: 260, overflowY: "auto" }}
        >
          {actionMessage}
        </Typography>
      )}
    </Box>
  );
};

export default DevIdeToolbar;
